import {SurveyBlackoutOffsets} from "./surveyBlackoutOffsets";

/** One blacked-out run of a text field, identified so a bar can be armed and restored. */
export interface SurveyBlackoutRange extends SurveyBlackoutOffsets {
    id: string
}

export interface SurveyBlackoutSegment {
    key: string
    text: string
    /** Set on the runs that render as a bar. */
    range?: SurveyBlackoutRange
}

const byStart = (a: SurveyBlackoutOffsets, b: SurveyBlackoutOffsets) => a.start - b.start;

/**
 * The one segment builder: text + ranges -> alternating plain and blacked-out runs. Shared with the
 * read-only side (`surveyRedactionSegments`), so the editor and the masked view cut a string alike.
 *
 * Ranges are clamped to the text and applied in order; a range that would rewind the cursor (an
 * overlap that slipped through) is skipped rather than allowed to duplicate characters, so the
 * concatenated segments always reproduce the input string exactly.
 */
export const buildSurveyBlackoutSegments = (
    text: string,
    ranges: SurveyBlackoutRange[]
): SurveyBlackoutSegment[] => {
    const segments: SurveyBlackoutSegment[] = [];
    let cursor = 0;

    ranges.slice().sort(byStart).forEach(range => {
        const start = Math.max(cursor, Math.min(range.start, text.length));
        const end = Math.max(start, Math.min(range.end, text.length));
        if (end <= start) return;

        if (start > cursor) segments.push({key: `plain-${cursor}`, text: text.slice(cursor, start)});
        segments.push({key: range.id, text: text.slice(start, end), range});
        cursor = end;
    });

    if (cursor < text.length) segments.push({key: `plain-${cursor}`, text: text.slice(cursor)});

    return segments;
};

/**
 * Overlap policy "merge": a new range swallows every range it touches, so dragging across two bars
 * and the gap between them leaves one bar, not three. The server unions ranges the same way, so a
 * caller that merges optimistically shows what the response is about to confirm.
 */
export const mergeSurveyBlackoutRange = (
    existing: SurveyBlackoutRange[],
    next: SurveyBlackoutOffsets,
    id: string
): SurveyBlackoutRange[] => {
    const touched = existing.filter(entry => entry.start <= next.end && entry.end >= next.start);
    const kept = existing.filter(entry => touched.indexOf(entry) === -1);
    const merged: SurveyBlackoutRange = {
        id,
        start: touched.reduce((min, entry) => Math.min(min, entry.start), next.start),
        end: touched.reduce((max, entry) => Math.max(max, entry.end), next.end),
    };

    return kept.concat(merged).sort(byStart);
};
